import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Paper, 
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    FormControlLabel,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Checkbox,
    TablePagination,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { optimizationService } from '../services/optimization.service';
import { orderService } from '../services/order.service';
import { forecastRunsService } from '../services/forecastruns.service';
import PODetailsDialog from './PODetailsDialog';

const InventoryOptimization = () => {
    const [latestRun, setLatestRun] = useState(null);
    const [allRuns, setAllRuns] = useState([]);
    const [selectedRunId, setSelectedRunId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogContent, setDialogContent] = useState(null);
    const [dialogTitle, setDialogTitle] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [forecastRunDetails, setForecastRunDetails] = useState(null);
    const [orderData, setOrderData] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [showReleaseDialog, setShowReleaseDialog] = useState(false);
    const [filterDraft, setFilterDraft] = useState(false);
    const [showOrderDialog, setShowOrderDialog] = useState(false);
    const [selectedPoNumber, setSelectedPoNumber] = useState(null);
    const [releaseProgress, setReleaseProgress] = useState({ current: 0, total: 0 });
    const [releaseInProgress, setReleaseInProgress] = useState(false);
    const [releaseResults, setReleaseResults] = useState({ success: 0, failed: 0, errors: [] });
    
    // Pagination state for orders table
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    
    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    // Status colors for PO status chips
    const statusColors = {
        'DRAFT': '#9e9e9e',
        'PREPARED': '#2196f3',
        'SENT': '#ff9800',
        'ACKNOWLEDGED': '#8bc34a',
        'SHIPPED': '#3f51b5',
        'DELIVERED': '#4caf50',
        'CANCELLED': '#f44336',
        'ERROR': '#d32f2f',
        'RECEIVED': '#009688',
        'RELEASED': '#673ab7'
    };

    useEffect(() => {
        fetchAllRuns();
    }, []);

    useEffect(() => {
        if (selectedRunId) {
            fetchRunDetails(selectedRunId);
        }
    }, [selectedRunId]);

    useEffect(() => {
        if (latestRun?.run_metadata?.inventory_run_id) {
            fetchOrderData(latestRun.run_metadata.inventory_run_id);
        }
    }, [latestRun]);

    const fetchAllRuns = async () => {
        try {
            setLoading(true);
            const data = await optimizationService.getOptimizationRuns();
            
            if (data.items && data.items.length > 0) {
                // Sort runs by timestamp in descending order
                const sortedRuns = data.items.sort((a, b) => {
                    return new Date(b.created_at) - new Date(a.created_at);
                });
                
                setAllRuns(sortedRuns);
                
                // Set the latest run as the default selected run
                setSelectedRunId(sortedRuns[0].inventory_run_id);
            }
        } catch (err) {
            setError('Failed to fetch optimization run data');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchRunDetails = async (runId) => {
        try {
            setLoading(true);
            const detailData = await optimizationService.getOptimizationRunById(runId);
            setLatestRun(detailData);
        } catch (err) {
            setError(`Failed to fetch details for run ${runId}`);
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrderData = async (runId) => {
        try {
            console.log('Fetching orders for run:', runId);
            const data = await orderService.getOrdersByRunId(runId);
            console.log('Received order data:', data);
            setOrderData(data.summaries || []);
        } catch (error) {
            console.error('Error fetching order data:', error);
        }
    };

    const handleShowConfig = (title, content) => {
        setDialogTitle(title);
        setDialogContent(JSON.stringify(content, null, 2));
        setOpenDialog(true);
    };

    const handleShowStores = () => {
        setDialogTitle('Store List');
        const storeList = latestRun.scope.store_ids.map(store => store).join('\n');
        setDialogContent(storeList);
        setOpenDialog(true);
    };

    const fetchForecastRunDetails = async (runId) => {
        try {
            // Using forecastRunsService.getForecastRunById for a more direct approach
            const data = await forecastRunsService.getForecastRunById(runId);
            
            if (data.status === 'success') {
                const runDetails = data.data;
                setDialogTitle('Forecast Run Details');
                setDialogContent(
                    <Box>
                        <Typography variant="h6">Run Summary</Typography>
                        <Typography>Algorithm: {runDetails.algorithm_name} v{runDetails.algorithm_version}</Typography>
                        <Typography>Status: {runDetails.status}</Typography>
                        <Typography>SKUs: {runDetails.sku_count}</Typography>
                        <Typography>Stores: {runDetails.store_count}</Typography>
                        <Typography>Created: {new Date(runDetails.run_timestamp).toLocaleString()}</Typography>
                        
                        <Typography variant="h6" sx={{ mt: 3 }}>Forecast Period</Typography>
                        <Typography>Start: {runDetails.forecast_start_date}</Typography>
                        <Typography>End: {runDetails.forecast_end_date}</Typography>
                        <Typography>History Start: {runDetails.history_start_date}</Typography>
                        <Typography>History End: {runDetails.history_end_date}</Typography>
                        
                        <Accordion sx={{ mt: 3 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" sx={{ m: 0 }}>Parameters (Raw)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box 
                                    component="pre" 
                                    sx={{ 
                                        whiteSpace: 'pre-wrap', 
                                        wordBreak: 'break-word',
                                        bgcolor: 'grey.100',
                                        p: 2,
                                        borderRadius: 1,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {JSON.stringify(runDetails.parameters, null, 2)}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                );
                setOpenDialog(true);
            } else {
                throw new Error(data.message || 'Failed to fetch forecast run details');
            }
        } catch (error) {
            console.error('Error fetching forecast run details:', error);
            setDialogTitle('Error');
            setDialogContent('Failed to fetch forecast run details');
            setOpenDialog(true);
        }
    };

    const ClickableValue = ({ onClick, children }) => (
        <Box 
            onClick={onClick}
            sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                    color: 'primary.main',
                },
                '& .MuiSvgIcon-root': {
                    opacity: 0.7,
                    fontSize: '1rem'
                }
            }}
        >
            <Typography>{children}</Typography>
            <InfoOutlinedIcon />
        </Box>
    );

    // Filter and pagination logic
    const filteredData = orderData.filter(order => {
        const matchesSearch = searchTerm === '' || 
            order.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.store.store_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.supplier.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.status.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesDraftFilter = !filterDraft || order.status === 'DRAFT';
        
        return matchesSearch && matchesDraftFilter;
    });

    const paginatedData = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Selection handling
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            setSelectedOrders(paginatedData);
        } else {
            setSelectedOrders([]);
        }
    };

    const handleClick = (event, order) => {
        const selectedIndex = selectedOrders.findIndex(selected => selected.po_number === order.po_number);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedOrders, order);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedOrders.slice(1));
        } else if (selectedIndex === selectedOrders.length - 1) {
            newSelected = newSelected.concat(selectedOrders.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedOrders.slice(0, selectedIndex),
                selectedOrders.slice(selectedIndex + 1),
            );
        }

        setSelectedOrders(newSelected);
    };

    const isSelected = (order) => selectedOrders.findIndex(selected => selected.po_number === order.po_number) !== -1;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterDraft(false);
        setPage(0);
    };

    const handleRelease = () => {
        setShowReleaseDialog(true);
    };

    const handleReleaseConfirm = async () => {
        try {
            setReleaseInProgress(true);
            setReleaseProgress({ current: 0, total: selectedOrders.length });
            setReleaseResults({ success: 0, failed: 0, errors: [] });
            
            // Create a copy of the results to update during processing
            let results = { success: 0, failed: 0, errors: [] };
            
            // Process each order sequentially
            for (let i = 0; i < selectedOrders.length; i++) {
                const order = selectedOrders[i];
                setReleaseProgress({ 
                    current: i + 1, 
                    total: selectedOrders.length 
                });
                
                try {
                    // Using orderService instead of direct API call
                    const response = await orderService.updateOrderStatus(order.po_number, 'RELEASED');
                    
                    results.success++;
                } catch (error) {
                    results.failed++;
                    results.errors.push({
                        po_number: order.po_number,
                        error: error.message || 'Network error'
                    });
                }
                
                // Update results state after each order
                setReleaseResults({...results});
            }
            
            // Refresh order data after all updates
            if (latestRun?.run_metadata?.inventory_run_id) {
                await fetchOrderData(latestRun.run_metadata.inventory_run_id);
            }
            
        } catch (error) {
            console.error('Error releasing orders:', error);
        } finally {
            setReleaseInProgress(false);
            // Keep dialog open to show results
        }
    };

    const handleOrderClick = async (order) => {
        setSelectedPoNumber(order.po_number);
        setShowOrderDialog(true);
    };

    // Enhanced Release Confirmation Dialog
    const ReleaseDialog = () => (
        <Dialog
            open={showReleaseDialog}
            onClose={() => !releaseInProgress && setShowReleaseDialog(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {releaseInProgress ? 'Releasing Orders...' : 'Confirm Release'}
            </DialogTitle>
            <DialogContent>
                {!releaseInProgress && releaseResults.success === 0 && releaseResults.failed === 0 ? (
                    <Typography>
                        {`${selectedOrders.length} order${selectedOrders.length === 1 ? '' : 's'} will be released to send to suppliers. Please confirm.`}
                    </Typography>
                ) : (
                    <Box>
                        {releaseInProgress ? (
                            <Box sx={{ width: '100%', mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ width: '100%', mr: 1 }}>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={(releaseProgress.current / releaseProgress.total) * 100} 
                                        />
                                    </Box>
                                    <Box sx={{ minWidth: 35 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {`${releaseProgress.current}/${releaseProgress.total}`}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    {`Processing order ${releaseProgress.current} of ${releaseProgress.total}`}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6">Release Results</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Chip 
                                        label={`${releaseResults.success} Successful`} 
                                        color="success" 
                                        variant="outlined" 
                                        icon={<CheckCircleOutlineIcon />}
                                    />
                                    <Chip 
                                        label={`${releaseResults.failed} Failed`} 
                                        color="error" 
                                        variant="outlined"
                                        icon={<ErrorOutlineIcon />} 
                                    />
                                </Box>
                                
                                {releaseResults.errors.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2">Errors:</Typography>
                                        <List dense>
                                            {releaseResults.errors.map((error, index) => (
                                                <ListItem key={index}>
                                                    <ListItemText 
                                                        primary={`PO: ${error.po_number}`}
                                                        secondary={error.error}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!releaseInProgress && (
                    <>
                        <Button 
                            onClick={() => setShowReleaseDialog(false)}
                            disabled={releaseInProgress}
                        >
                            {releaseResults.success > 0 || releaseResults.failed > 0 ? 'Close' : 'Cancel'}
                        </Button>
                        {releaseResults.success === 0 && releaseResults.failed === 0 && (
                            <Button 
                                onClick={handleReleaseConfirm} 
                                color="primary"
                                disabled={releaseInProgress}
                            >
                                Confirm
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );

    const handleRunChange = (event) => {
        setSelectedRunId(event.target.value);
    };

    // Format date for display in the dropdown
    const formatRunDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" sx={{ p: 2 }}>
                {error}
            </Typography>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Inventory Optimization
                </Typography>
                
                {/* Run selection dropdown */}
                <FormControl sx={{ minWidth: 300 }}>
                    <InputLabel id="run-select-label">Select Optimization Run</InputLabel>
                    <Select
                        labelId="run-select-label"
                        id="run-select"
                        value={selectedRunId || ''}
                        label="Select Optimization Run"
                        onChange={handleRunChange}
                        disabled={loading || allRuns.length === 0}
                    >
                        {allRuns.map((run) => (
                            <MenuItem key={run.inventory_run_id} value={run.inventory_run_id}>
                                {run.inventory_run_id} ({formatRunDate(run.created_at)})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {latestRun && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={3}>
                        {/* First Row: Basic Info */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Latest Run</Typography>
                            <Typography>{latestRun.run_metadata.inventory_run_id}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Created At</Typography>
                            <Typography>
                                {new Date(latestRun.run_metadata.created_at).toLocaleString()}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Completed At</Typography>
                            <Typography>
                                {latestRun.run_metadata.completed_at ? 
                                    new Date(latestRun.run_metadata.completed_at).toLocaleString() : 
                                    'N/A'}
                            </Typography>
                        </Grid>

                        {/* Second Row: Scope Info */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Total Stores</Typography>
                            <ClickableValue onClick={handleShowStores}>
                                {latestRun.scope.store_count}
                            </ClickableValue>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Active Products</Typography>
                            <Typography>{latestRun.scope.active_products_count}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Status</Typography>
                            <Typography>{latestRun.run_metadata.status}</Typography>
                        </Grid>

                        {/* Third Row: Configuration */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Forecast Run</Typography>
                            <ClickableValue 
                                onClick={() => fetchForecastRunDetails(latestRun.configuration.forecast_run_id)}
                            >
                                {latestRun.configuration.forecast_run_id}
                            </ClickableValue>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Inventory Parameters</Typography>
                            <ClickableValue 
                                onClick={() => handleShowConfig(
                                    'Inventory Parameters',
                                    latestRun.configuration.inventory_parameters
                                )}
                            >
                                {`${latestRun.configuration.inventory_parameters?.global_parameters?.inventory_optimization?.algorithm || 'N/A'} `}
                                {`(v${latestRun.configuration.inventory_parameters?.global_parameters?.version || 'N/A'})`}
                            </ClickableValue>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Sourcing Configuration</Typography>
                            <ClickableValue 
                                onClick={() => handleShowConfig(
                                    'Sourcing Configuration',
                                    latestRun.configuration.sourcing_config
                                )}
                            >
                                {`Default: ${latestRun.configuration.sourcing_config?.global_defaults?.sourcing_algorithm || 'N/A'}`}
                            </ClickableValue>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Orders Table Section */}
            <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Draft Replenishment Orders
                </Typography>
                
                {/* Search and Filter Controls */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Search by PO Number, Store, Supplier, or Status..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ minWidth: 300 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={filterDraft}
                                onChange={(e) => setFilterDraft(e.target.checked)}
                            />
                        }
                        label="Show Draft Orders Only"
                    />
                    {(searchTerm || filterDraft) && (
                        <Button size="small" onClick={clearFilters}>
                            Clear Filters
                        </Button>
                    )}
                </Box>

                {/* Orders Table */}
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectedOrders.length > 0 && selectedOrders.length < paginatedData.length}
                                            checked={paginatedData.length > 0 && selectedOrders.length === paginatedData.length}
                                            onChange={handleSelectAllClick}
                                        />
                                    </TableCell>
                                    <TableCell>PO Number</TableCell>
                                    <TableCell>Store</TableCell>
                                    <TableCell>Supplier</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Order Date</TableCell>
                                    <TableCell>Total Amount</TableCell>
                                    <TableCell>Line Items</TableCell>
                                    <TableCell>Items Ordered</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((order) => {
                                    const isItemSelected = isSelected(order);
                                    return (
                                        <TableRow
                                            hover
                                            key={order.po_number}
                                            selected={isItemSelected}
                                            onClick={(event) => handleClick(event, order)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isItemSelected} />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Click to view order details">
                                                    <span 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOrderClick(order);
                                                        }}
                                                        style={{ 
                                                            cursor: 'pointer', 
                                                            color: '#1976d2', 
                                                            textDecoration: 'underline' 
                                                        }}
                                                    >
                                                        {order.po_number}
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{order.store.store_name}</TableCell>
                                            <TableCell>{order.supplier.supplier_name}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={order.status}
                                                    size="small"
                                                    sx={{ 
                                                        backgroundColor: statusColors[order.status] || '#9e9e9e',
                                                        color: 'white',
                                                        '& .MuiChip-label': {
                                                            fontWeight: 500
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {order.dates.order_date ? 
                                                    new Date(order.dates.order_date).toLocaleDateString() : 
                                                    'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                ${order.financial.total_amount?.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                }) || '0.00'}
                                            </TableCell>
                                            <TableCell>{order.items.total_lines}</TableCell>
                                            <TableCell>
                                                {order.items.total_ordered?.toLocaleString() || '0'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[50, 100, 200, 500]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>

                <Box sx={{ mt: 2 }}>
                    <Button 
                        variant="contained" 
                        color="primary"
                        disabled={selectedOrders.length === 0}
                        onClick={handleRelease}
                    >
                        Release Orders ({selectedOrders.length})
                    </Button>
                </Box>
            </Paper>

            {/* Release Confirmation Dialog */}
            <ReleaseDialog />

            {/* PO Details Dialog */}
            <PODetailsDialog
                open={showOrderDialog}
                onClose={() => setShowOrderDialog(false)}
                poNumber={selectedPoNumber}
            />

            {/* Configuration Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Box 
                        component="pre" 
                        sx={{ 
                            whiteSpace: 'pre-wrap', 
                            wordWrap: 'break-word',
                            bgcolor: 'grey.100',
                            p: 2,
                            borderRadius: 1,
                            fontSize: '0.875rem'
                        }}
                    >
                        {dialogContent}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default InventoryOptimization; 